/**
 * Authenticated file downloads from the Api (Warren, 29 Oct 2026).
 *
 * The Api reads only the `Authorization: Bearer` header (plugins/auth.ts).
 * A plain `<a href download>` or `window.open` to an Api route is a
 * top-level navigation: it carries cookies but never the token Admin keeps
 * in memory, so the Api answers 401 "Authentication required" and the
 * browser saves the JSON error (or nothing). That broke the driver
 * profile's "Download PDF" and "Export CSV", the group modal's pair and the
 * fleet Sessions "Export CSV".
 *
 * Every Admin download now goes through the signed-in ky client instead
 * (lib/downloadAuthed.ts wires `api` in): the Bearer token rides the
 * beforeRequest hook, and a 401 gets the normal shared refresh and one
 * replay (lib/sessionRefresh.ts), exactly as every other call. Then:
 *   1. read the body as a Blob;
 *   2. name the file from Content-Disposition (filename*, then filename),
 *      else the caller's fallback;
 *   3. save it through an object URL and a temporary `a[download]` click,
 *      then revoke the URL;
 *   4. on failure throw a DownloadError whose message says why (not signed
 *      in, no permission, not found, not ready, rate limited, server error,
 *      timed out, network), so the page can show it as is.
 *
 * A second call for the same target while the first is in flight returns
 * the first call's promise: a double click never downloads twice. Pages
 * also disable the button while it is busy (hooks/useAuthedDownload.ts).
 *
 * Kept free of `api`, the auth store and `import.meta.env` so it runs under
 * node:test (lib/download.test.ts).
 */

export type DownloadFailure =
  | 'signed_out' | 'forbidden' | 'not_found' | 'not_ready' | 'rate_limited'
  | 'server' | 'timeout' | 'network' | 'invalid' | 'other';

/** Messages a page shows as they are. No dashes, one reason each. */
export const DOWNLOAD_MESSAGES: Record<Exclude<DownloadFailure, 'other' | 'invalid'>, string> = {
  signed_out: 'Download failed: you are not signed in. Sign in again, then retry.',
  forbidden: 'Download failed: your account does not have permission to download this file.',
  not_found: 'Download failed: not found. It may have been removed, or it is outside your access.',
  not_ready: 'Download failed: this file is not available yet.',
  rate_limited: 'Download failed: too many requests. Wait a moment, then retry.',
  server: 'Download failed: the server could not make the file. Try again in a moment.',
  timeout: 'Download failed: the server took too long to answer. Try again in a moment.',
  network: 'Download failed: the request did not reach the server. Check your connection, then retry.',
};

export class DownloadError extends Error {
  readonly reason: DownloadFailure;
  readonly status: number | null;
  /** The Api's response, unread, so getDisplayErrorMessage still works. */
  readonly response?: Response;
  constructor(reason: DownloadFailure, message: string, status: number | null, response?: Response) {
    super(message);
    this.name = 'DownloadError';
    this.reason = reason;
    this.status = status;
    if (response) this.response = response;
  }
}

/** A name a browser will save: no path separators, quotes or control
 *  characters, never empty. */
function cleanFilename(name: string): string {
  return name.replace(/[\\/:*?"<>|\u0000-\u001f]+/g, '_').replace(/^[.\s]+/, '').trim();
}

/**
 * The filename in a Content-Disposition header: RFC 5987 `filename*`
 * first, then `filename` (quoted or bare), else the fallback.
 */
export function filenameFromDisposition(header: string | null | undefined, fallback: string): string {
  const value = header ?? '';
  const star = /filename\*\s*=\s*([^;]+)/i.exec(value);
  if (star) {
    const raw = (star[1] ?? '').trim().replace(/^"(.*)"$/, '$1');
    const encoded = raw.includes("''") ? raw.slice(raw.indexOf("''") + 2) : raw;
    try {
      const name = cleanFilename(decodeURIComponent(encoded));
      if (name) return name;
    } catch {
      // A malformed escape: fall through to the plain filename.
    }
  }
  const plain = /filename\s*=\s*(?:"([^"]*)"|([^;]+))/i.exec(value);
  const name = cleanFilename((plain?.[1] ?? plain?.[2] ?? '').trim());
  return name || cleanFilename(fallback) || 'download';
}

/**
 * The ky-relative path for a download target. Accepts a ky path
 * ('admin/drivers/x/reports/pdf'), the same with a leading slash, or a full
 * URL under the Api base (absolute or the '/api' dev proxy). Anything else
 * is refused: the signed-in token must never ride to another host.
 */
export function apiPathFor(target: string, apiBase: string): string {
  const base = apiBase.replace(/\/+$/, '');
  if (base && (target === base || target.startsWith(`${base}/`) || target.startsWith(`${base}?`))) {
    return target.slice(base.length).replace(/^\/+/, '');
  }
  if (/^[a-z][a-z0-9+.-]*:/i.test(target) || target.startsWith('//')) {
    throw new DownloadError('invalid', 'Download failed: that link is not an EVI Api address.', null);
  }
  return target.replace(/^\/+/, '');
}

/** The server's own message, only for a VALIDATION_ERROR (the same rule as
 *  getDisplayErrorMessage in lib/api.ts: other codes may be internal). */
async function validationMessage(response: Response): Promise<string | null> {
  try {
    const body = await response.clone().json() as { error?: { code?: unknown; message?: unknown } } | null;
    const msg = body?.error?.code === 'VALIDATION_ERROR' ? body.error.message : null;
    return typeof msg === 'string' && msg.trim() ? `Download failed: ${msg.trim()}` : null;
  } catch {
    return null;
  }
}

/** Sort a failed download into a DownloadError with a clear message. */
export async function downloadErrorFor(err: unknown): Promise<DownloadError> {
  if (err instanceof DownloadError) return err;
  const response = (err as { response?: unknown } | null | undefined)?.response;
  if (response instanceof Response) {
    const status = response.status;
    const reason: DownloadFailure = status === 401 ? 'signed_out'
      : status === 403 ? 'forbidden'
      : status === 404 ? 'not_found'
      : status === 409 ? 'not_ready'
      : status === 429 ? 'rate_limited'
      : status >= 500 ? 'server'
      : 'other';
    const message = reason === 'other'
      ? (await validationMessage(response)) ?? `Download failed (HTTP ${status}).`
      : DOWNLOAD_MESSAGES[reason];
    return new DownloadError(reason, message, status, response);
  }
  if ((err as { name?: unknown } | null | undefined)?.name === 'TimeoutError') {
    return new DownloadError('timeout', DOWNLOAD_MESSAGES.timeout, null);
  }
  return new DownloadError('network', DOWNLOAD_MESSAGES.network, null);
}

export interface SaveEnv {
  document: Pick<Document, 'createElement' | 'body'>;
  url: { createObjectURL(blob: Blob): string; revokeObjectURL(url: string): void };
  /** Revocation waits a moment so Safari finishes starting the save. */
  later: (fn: () => void, ms: number) => unknown;
}

const browserEnv = (): SaveEnv => ({
  document,
  url: URL,
  later: (fn, ms) => setTimeout(fn, ms),
});

/** Save a Blob as `filename`: object URL, temporary a[download] click,
 *  anchor removed, URL revoked. */
export function saveBlob(blob: Blob, filename: string, env: SaveEnv = browserEnv()): void {
  const objectUrl = env.url.createObjectURL(blob);
  const a = env.document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  a.rel = 'noopener';
  a.style.display = 'none';
  env.document.body.appendChild(a);
  try {
    a.click();
  } finally {
    a.remove();
    env.later(() => env.url.revokeObjectURL(objectUrl), 1000);
  }
}

export interface DownloaderDeps {
  /** GET a ky-relative path through the signed-in client; resolves with a
   *  2xx response, throws ky's HTTPError otherwise. */
  get: (path: string) => Promise<Response>;
  /** The Api base the absolute targets may start with. */
  apiBase: string;
  save?: (blob: Blob, filename: string) => void;
}

export interface Downloader {
  /** Fetch `target` with the signed-in token and save it. Throws a
   *  DownloadError on failure. */
  download: (target: string, fallbackFilename: string) => Promise<void>;
  /** True while a download of `target` is in flight. */
  isInFlight: (target: string) => boolean;
}

export function createDownloader(deps: DownloaderDeps): Downloader {
  const inFlight = new Map<string, Promise<void>>();
  const save = deps.save ?? ((blob: Blob, filename: string) => saveBlob(blob, filename));

  async function run(path: string, fallbackFilename: string): Promise<void> {
    let response: Response;
    let blob: Blob;
    try {
      response = await deps.get(path);
      blob = await response.blob();
    } catch (err) {
      throw await downloadErrorFor(err);
    }
    save(blob, filenameFromDisposition(response.headers.get('content-disposition'), fallbackFilename));
  }

  return {
    download(target, fallbackFilename) {
      let path: string;
      try {
        path = apiPathFor(target, deps.apiBase);
      } catch (err) {
        return Promise.reject(err);
      }
      const existing = inFlight.get(path);
      if (existing) return existing;
      const p = run(path, fallbackFilename).finally(() => { inFlight.delete(path); });
      inFlight.set(path, p);
      return p;
    },
    isInFlight(target) {
      try {
        return inFlight.has(apiPathFor(target, deps.apiBase));
      } catch {
        return false;
      }
    },
  };
}

/** A ky path with its query; undefined, null and empty values dropped. */
export function pathWithQuery(path: string, query?: Record<string, string | undefined | null>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(query ?? {})) {
    if (v !== undefined && v !== null && v !== '') sp.set(k, v);
  }
  const qs = sp.toString();
  return qs ? `${path}${path.includes('?') ? '&' : '?'}${qs}` : path;
}
